/**
 * Excel import for Early Signals: parse .xlsx/.xls and map to suppliers (and optional news).
 * Supports flexible column names (Excel Table 4 names or simple names).
 */

import * as XLSX from 'xlsx'
import { getCoordinatesForNews, pickDisplayRegion } from './regionToCoordinates'
import { matchSupplierId } from './companyNameMatch'

/**
 * Direct/Indirect template headers — aligned to "For Sai - The Simple" workbook layout.
 * Shared risk + master columns; Indirect adds Supplier Parent Name after Entity Type.
 */
const RISK_DIMENSION_HEADERS = [
  'Strategic Risk',
  'Financial Risk',
  'Compliance & Legal Risk',
  'Operational Risk',
  'Geopolitical Risk',
  'Infrastructure & Utilities Risk',
  'Supply Chain Risk',
  'ESG Risk',
  'Labor & Workforce Risk',
  'Health & Biological Risk',
  'Natural Hazard Risk',
  'Market Risk',
  'Technology & Innovation Risk',
  'Cybersecurity Risk',
] as const

const RISK_COMMENTARY_HEADERS = [
  'Strategic Risk – Commentary',
  'Financial Risk – Commentary',
  'Compliance & Legal Risk – Commentary',
  'Operational Risk – Commentary',
  'Geopolitical Risk – Commentary',
  'Infrastructure & Utilities Risk – Commentary',
  'Supply Chain Risk – Commentary',
  'ESG Risk – Commentary',
  'Labor & Workforce Risk – Commentary',
  'Health & Biological Risk – Commentary',
  'Natural Hazard Risk – Commentary',
  'Market Risk – Commentary',
  'Technology & Innovation Risk – Commentary',
  'Cybersecurity Risk – Commentary',
] as const

const DIRECT_TEMPLATE_HEADERS = [
  'Serial #',
  'Entity Type',
  'Supplier Name',
  'Supplier ID',
  'Supplier Location',
  'Region',
  'Brand Name',
  'Category',
  'Sub-Category',
  'DM/EM',
  'Single Source',
  'Private/Public',
  'Plant Desc',
  'Spend',
  ...RISK_DIMENSION_HEADERS,
  'Final Risk',
  'Highest Risk Areas',
  'Next Best Action',
  ...RISK_COMMENTARY_HEADERS,
] as const

const INDIRECT_TEMPLATE_HEADERS = [
  'Serial #',
  'Entity Type',
  'Supplier Parent Name',
  'Supplier Name',
  'Supplier ID',
  'Supplier Location',
  'Region',
  'Brand Name',
  'Category',
  'Sub-Category',
  'DM/EM',
  'Single Source',
  'Private/Public',
  'Plant Desc',
  'Spend',
  ...RISK_DIMENSION_HEADERS,
  'Final Risk',
  'Highest Risk Areas',
  'Next Best Action',
  ...RISK_COMMENTARY_HEADERS,
] as const

const USER_GROUPS_TEMPLATE_HEADERS = ['Function', 'Parent Group', 'Subgroup'] as const

/** Mitigation Tracker headers — aligned to Master Database Aug 08 sheet. */
const MITIGATION_TRACKER_TEMPLATE_HEADERS = [
  'Date',
  'Alert Description',
  'Supplier Name',
  'Location',
  'Kenvue Region',
  'Plant Code',
  'Plant Desc.',
  'Brand Name',
  'Single Source (Yes/No)',
  'Category',
  'Sub-Category',
  'Website',
  'Ownership',
  'Supplier Unique ID',
  'Risk Score',
  'Highest Risk Areas',
  'Category Manager',
  'Regional Head',
  'Status',
  'Comments',
] as const

/** Build and download Direct/Indirect workbook template (+ user groups + mitigation). */
export function downloadSuppliersTemplate(): void {
  const dims = [5, 2, 2, 2, 2, 2, 2, 3, 2, 1, 5, 2, 1, 1]
  const commentaries = [
    'Example strategic commentary for this supplier–brand line.',
    'Example financial commentary.',
    'Example compliance commentary.',
    'Example operational commentary.',
    'Example geopolitical commentary.',
    'Example infrastructure commentary.',
    'Example supply chain commentary.',
    'Example ESG commentary.',
    'Example labor commentary.',
    'Example health & biological commentary.',
    'Example natural hazard commentary.',
    'Example market commentary.',
    'Example technology commentary.',
    'Example cybersecurity commentary.',
  ]
  const exampleDirectRow = [
    1,
    'Direct',
    'Example Supplier Ltd',
    '21290',
    'Australia',
    'APAC',
    'Example Brand',
    'External Manufacturing',
    'Others',
    'EM',
    'No',
    'Private',
    'EM Supplier',
    125000,
    ...dims,
    '2.5/10',
    'Natural Hazard Risk, Strategic Risk',
    'Refresh BCP; track labor KPIs in QBRs.',
    ...commentaries,
  ]
  const exampleIndirectRow = [
    1,
    'Indirect',
    'Example Parent Co',
    'Example Agency LLC',
    '99001',
    'United States',
    'NA',
    '',
    'Media',
    'Global Media Agencies',
    '',
    'No',
    'Private',
    '',
    45000,
    ...dims,
    '3.1/10',
    'Operational Risk, Market Risk',
    'Review SOW renewals and dependency concentration.',
    ...commentaries,
  ]
  const wsDirect = XLSX.utils.aoa_to_sheet([
    DIRECT_TEMPLATE_HEADERS as unknown as string[],
    exampleDirectRow,
  ])
  const wsIndirect = XLSX.utils.aoa_to_sheet([
    INDIRECT_TEMPLATE_HEADERS as unknown as string[],
    exampleIndirectRow,
  ])
  const wsGroups = XLSX.utils.aoa_to_sheet([
    USER_GROUPS_TEMPLATE_HEADERS as unknown as string[],
    ['Procurement', 'Indirect', 'Media'],
  ])
  const wsMitigation = XLSX.utils.aoa_to_sheet([
    MITIGATION_TRACKER_TEMPLATE_HEADERS as unknown as string[],
    [
      '20-Apr-26',
      'Example alert: capacity disruption risk',
      'Example Supplier Ltd',
      'Australia',
      'APAC',
      'Not Available',
      'EM Supplier',
      'Example Brand',
      'No',
      'External Manufacturing',
      'Others',
      'https://example.com',
      'Private',
      '21290',
      '2.5',
      'Natural Hazard Risk, Strategic Risk',
      '',
      '',
      'open',
      '',
    ],
  ])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, wsDirect, 'Direct with Commentary')
  XLSX.utils.book_append_sheet(wb, wsIndirect, 'Indirect with Commentary')
  XLSX.utils.book_append_sheet(wb, wsGroups, 'User groups risk monitoring')
  XLSX.utils.book_append_sheet(wb, wsMitigation, 'Mitigation Tracker')
  XLSX.writeFile(wb, 'direct_indirect_import_template.xlsx')
}

const SUPPLIER_HEADER_ALIASES: Record<string, string> = {
  supplier: 'name',
  name: 'name',
  'supplier name': 'name',
  'entity name': 'name',
  entity: 'name',
  vendor: 'name',
  'vendor name': 'name',
  company: 'name',
  'company name': 'name',
  location: 'location',
  'location / region': 'location',
  'supplier location': 'location',
  // Region = Kenvue region. Country is preferred geo location when both Country and
  // Supplier Location are present (Indirect sheets); see mapRowToSupplier.
  region: 'kenvue_region',
  country: 'country',
  geography: 'location',
  commodity: 'industry',
  industry: 'industry',
  'risk rating': 'risk_rating',
  risk_rating: 'risk_rating',
  // Template / Admin form "Risk category (optional)" → risk_rating (+ gauge when label-like)
  'risk category': 'risk_rating',
  'risk category (optional)': 'risk_rating',
  'risk category optional': 'risk_rating',
  revenue: 'revenue',
  growth: 'growth',
  employees: 'employees',
  'years in business': 'years_in_business',
  years_in_business: 'years_in_business',
  'lead time (days)': 'lead_time_days',
  'sla (%)': 'sla_pct',
  'quality (%)': 'quality_pct',
  'spend ($k)': 'spend_k',
  spend: 'spend_usd',
  'dm/em': 'dm_em',
  'dm em': 'dm_em',
  dmem: 'dm_em',
  'supplier parent name': 'parent_name',
  'parent name': 'parent_name',
  'parent supplier name': 'parent_name',
  'entity type': 'entity_type',
  'months to contract expiry': 'months_to_contract_expiry',
  // Score columns (resolved in mapRowToSupplier — do not last-write-wins collide):
  // - "Final Risk" (For Sai) → overall_score via numeric/x/10 parse; "Not Available" → null
  // - "Overall Risk Rating" → risk_gauge when Low/Moderate/High label; numeric/x/10 only when Final Risk absent (legacy)
  // - Table 4 "Overall Vulnerability Index (0-10)" → overall_score
  'overall vulnerability index (0-10)': 'overall_score',
  'risk score (0-10)': 'overall_score',
  'overall risk rating': 'overall_risk_rating',
  'overall risk': 'overall_score',
  'risk score': 'overall_score',
  vulnerability: 'overall_score',
  'overall risk score': 'overall_score',
  'overall_score': 'overall_score',
  outlook: 'outlook',
  'summary of risk ratings': 'outlook',
  summary: 'outlook',
  'summary of risk': 'outlook',
  'key risks': 'outlook',
  'risk summary': 'outlook',
  'outlook / summary': 'outlook',
  'executive summary': 'outlook',
  'executive risk summary': 'outlook',
  'key takeaways': 'key_takeaways',
  actions: 'actions',
  'next best action(s)': 'actions',
  'recommended actions': 'actions',
  'next steps': 'actions',
  'vendor performance kpi risk (1-10)': 'vendor_performance_kpi_risk',
  'sla compliance risk (1-10)': 'sla_compliance_risk',
  'contracts risk (1-10)': 'contracts_risk',
  'stakeholder feedback risk (1-10)': 'stakeholder_feedback_risk',
  'spend contribution risk (1-10)': 'spend_contribution_risk',
  // Table 4 extra columns (Excel: Invoices, Support, Penalties Clause, Auto Renew, Stakeholder Score)
  invoices: 'invoices',
  support: 'support',
  'penalties clause': 'penalties_clause',
  penalties_clause: 'penalties_clause',
  'auto renew': 'auto_renew',
  auto_renew: 'auto_renew',
  'stakeholder score (1-5)': 'stakeholder_score',
  stakeholder_score: 'stakeholder_score',
  // App dimension keys (Risk Matrix 8 columns)
  strategic: 'strategic',
  'strategic risk': 'strategic',
  compliance: 'compliance',
  'compliance & legal risk': 'compliance',
  operational: 'operational',
  ops: 'operational',
  'operational risk': 'operational',
  labor: 'labor',
  'labor & workforce risk': 'labor',
  infra: 'infra',
  'infrastructure & utilities risk': 'infra',
  geopolitical: 'geopolitical',
  geo: 'geopolitical',
  'geopolitical risk': 'geopolitical',
  esg: 'esg',
  'esg risk': 'esg',
  cyber: 'cyber',
  'cybersecurity risk': 'cyber',
  // Master Database Intelligence View / Kenvue Master
  'file name': 'assessment_file_name',
  'report title': 'report_title',
  'kenvue region': 'kenvue_region',
  'plant code': 'plant_code',
  'plant desc.': 'plant_desc',
  'plant desc': 'plant_desc',
  'serial #': 'source_serial',
  'serial#': 'source_serial',
  serial: 'source_serial',
  brand: 'brand',
  'brand name': 'brand',
  'single source (yes/no)': 'single_source',
  'single source': 'single_source',
  category: 'category',
  'sub-category': 'sub_category',
  'sub category': 'sub_category',
  website: 'website',
  ownership: 'ownership',
  'private/public': 'ownership',
  'supplier unique id': 'supplier_unique_id',
  'supplier id': 'supplier_unique_id',
  'risk gauge': 'risk_gauge',
  'final risk': 'overall_score',
  'highest risk areas': 'highest_risk_areas',
  'next best action': 'actions',
  'financial risk': 'financial',
  'supply chain risk': 'supply_chain',
  'health & biological risk': 'health_biological',
  'natural hazard risk': 'natural_hazard',
  'market risk': 'market',
  'technology & innovation risk': 'tech_innovation',
  'risk outlook': 'risk_outlook',
  'risk outlook (6 months)': 'risk_outlook_6m',
  'risk outlook (12 months)': 'risk_outlook_12m',
  recommendations: 'recommendations',
  'questions to ask supplier': 'questions_to_ask',
  methodology: 'methodology',
}

/** Dimension keys used for risk_scores / risk_dimension_narratives (Master / Kenvue). */
const MASTER_DIMENSION_KEY_SET = new Set([
  'strategic',
  'financial',
  'compliance',
  'operational',
  'geopolitical',
  'infra',
  'supply_chain',
  'esg',
  'labor',
  'health_biological',
  'natural_hazard',
  'market',
  'tech_innovation',
  'cyber',
])

/**
 * Map "Strategic Risk – Commentary" / "... - Commentary" headers to
 * `${dimension}_commentary` (en-dash or hyphen).
 */
function commentaryKeyFromHeader(normalizedHeader: string): string | null {
  const m = normalizedHeader.match(/^(.+?)\s*[–—-]\s*commentary\s*$/i)
  if (!m) return null
  const base = m[1].trim().toLowerCase().replace(/\s+/g, ' ')
  const dim = SUPPLIER_HEADER_ALIASES[base] ?? base
  if (!MASTER_DIMENSION_KEY_SET.has(dim)) return null
  return `${dim}_commentary`
}

/** Names that must never be treated as suppliers (risk types, table headers from docs Excel). */
const SUPPLIER_NAME_BLOCKLIST = new Set([
  'compliance & legal risk',
  'cybersecurity risk',
  'financial risk',
  'esg risk',
  'geopolitical risk',
  'health & biological risk',
  'infrastructure & utilities risk',
  'labor & workforce risk',
  'natural hazard risk',
  'operational risk',
  'market risk',
  'strategic risk',
  'supply chain risk',
  'technology & innovation risk',
  'table 1',
  'table 2',
  'table 3',
  'table 4',
  'instruction guide',
  'weights assigned',
  'internal data parameters',
  'internal data assessment weights',
  'composition weights',
  'data type',
  'data weights',
  'external data',
  'internal data',
  'weights',
  'category',
  'type of risk',
  // Literal header / empty
  'supplier',
  '(blank)',
  'blank',
  'na',
  'n/a',
  'tbd',
  '.',
  // Other table column headers used as row content
  'location',
  'commodity',
  'entity',
  'entity name',
  'lead time (days)',
  'sla (%)',
  'quality (%)',
  'spend ($k)',
  'months to contract expiry',
  'overall vulnerability index (0-10)',
  'key takeaways',
  'next best action(s)',
  'invoices',
  'support',
  'penalties clause',
  'auto renew',
  'helper: spend share',
  // Section / summary labels
  'key risks',
  'summary',
  'next steps',
  'risk summary',
  'overview',
  // Single-word risk/category names
  'strategic',
  'compliance',
  'financial',
  'market',
  'esg',
  'operational',
  'geopolitical',
  'labor',
  'natural',
  'supply',
  'technology',
  'health',
  'infrastructure',
  'cybersecurity',
])

/** Task sheet header aliases (Mitigation / Tasks second sheet). */
const TASK_HEADER_ALIASES: Record<string, string> = {
  'supplier name': 'supplier_name',
  supplier_name: 'supplier_name',
  supplier: 'supplier_name',
  task: 'task',
  'alert description': 'task',
  description: 'task',
  date: 'alert_date',
  'alert date': 'alert_date',
  status: 'status',
  priority: 'priority',
  comments: 'comments',
  comment: 'comments',
  'category manager': 'category_manager',
  'regional head': 'regional_head',
  location: 'plant_location',
  'plant location': 'plant_location',
  brand: 'brand',
  'brand name': 'brand',
  'supplier unique id': 'supplier_unique_id',
  'supplier id': 'supplier_unique_id',
  supplier_unique_id: 'supplier_unique_id',
}

export interface ParsedSheet {
  name: string
  rows: Record<string, unknown>[]
  headers: string[]
}

export interface SupplierRow {
  name: string
  location?: string | null
  risk_rating?: string | null
  industry?: string | null
  revenue?: string | null
  growth?: string | null
  employees?: string | null
  years_in_business?: number | null
  lead_time_days?: number | null
  sla_pct?: number | null
  quality_pct?: number | null
  spend_k?: number | null
  months_to_contract_expiry?: number | null
  overall_score?: number | null
  outlook?: string | null
  actions?: string | null
  key_takeaways?: string | null
  invoices?: string | null
  support?: string | null
  penalties_clause?: string | null
  auto_renew?: string | null
  stakeholder_score?: number | null
  risk_scores?: Record<string, unknown>
  supplier_unique_id?: string | null
  website?: string | null
  ownership?: string | null
  kenvue_region?: string | null
  plant_code?: string | null
  plant_desc?: string | null
  brand?: string | null
  single_source?: boolean | null
  category?: string | null
  sub_category?: string | null
  risk_gauge?: string | null
  highest_risk_areas?: string | null
  risk_outlook?: string | null
  risk_outlook_6m?: string | null
  risk_outlook_12m?: string | null
  recommendations?: string | null
  questions_to_ask?: string | null
  methodology?: string | null
  assessment_file_name?: string | null
  report_title?: string | null
  risk_dimension_narratives?: Record<string, string>
  dm_em?: string | null
  procurement_type?: 'direct' | 'indirect' | null
  parent_name?: string | null
  /** Excel Serial # — parse/merge only; keeps line-level Direct rows distinct. */
  source_serial?: string | null
}

/**
 * Stable key for preview dedupe / merge.
 * Direct: prefer Serial # when present; else unique_id/name + brand + category/sub + plant/geo/dm.
 * Indirect: unique_id+category+sub (else name+cat+sub).
 * Never unique_id alone — multi-brand / multi-category lines must stay distinct.
 */
export function supplierRowMergeKey(
  r: Pick<
    SupplierRow,
    | 'name'
    | 'brand'
    | 'supplier_unique_id'
    | 'category'
    | 'sub_category'
    | 'procurement_type'
    | 'plant_desc'
    | 'location'
    | 'kenvue_region'
    | 'dm_em'
    | 'source_serial'
  >
): string {
  const uid = (r.supplier_unique_id ?? '').trim().toLowerCase()
  const name = (r.name ?? '').trim().toLowerCase()
  const brand = (r.brand ?? '').trim().toLowerCase()
  const cat = (r.category ?? '').trim().toLowerCase()
  const sub = (r.sub_category ?? '').trim().toLowerCase()
  const plant = (r.plant_desc ?? '').trim().toLowerCase()
  const loc = (r.location ?? '').trim().toLowerCase()
  const reg = (r.kenvue_region ?? '').trim().toLowerCase()
  const dm = (r.dm_em ?? '').trim().toLowerCase()
  const serial = (r.source_serial ?? '').trim().toLowerCase()
  const pt = r.procurement_type === 'indirect' ? 'indirect' : 'direct'
  if (pt === 'indirect') {
    const idPart = uid || `n:${name}`
    return `ind:${idPart}|c:${cat}|s:${sub}`
  }
  if (serial) return `dir:serial:${serial}`
  const idPart = uid || `n:${name}`
  return `dir:${idPart}|b:${brand}|c:${cat}|s:${sub}|p:${plant}|l:${loc}|r:${reg}|d:${dm}`
}

/**
 * Merge two supplier rows (e.g. from Table 4 and Detailed Assessment). Prefer non-empty values;
 * merge risk_scores so we keep dimensions from whichever row has them. Prevents later sheet from
 * overwriting risk_scores with empty when multiple sheets are selected.
 *
 * Overall score (two CSV sources → one DB field):
 * - Table 4: "Overall Vulnerability Index (0-10)" = weighted internal KPIs (e.g. Delpharm 3.95).
 * - Detailed Assessment: "Overall Risk Rating" = narrative/external rating (e.g. Delpharm 4.2).
 * When both are present, prefer the row that has outlook or actions (Detailed Assessment), so the
 * narrative rating (4.2) is used in Risk Matrix / Mitigation instead of the internal index (3.95).
 */
export function mergeSupplierRows(existing: SupplierRow, next: SupplierRow): SupplierRow {
  const mergedRisk =
    (existing.risk_scores && Object.keys(existing.risk_scores).length > 0) || (next.risk_scores && Object.keys(next.risk_scores).length > 0)
      ? { ...(existing.risk_scores ?? {}), ...(next.risk_scores ?? {}) }
      : undefined
  const mergedNarratives =
    (existing.risk_dimension_narratives && Object.keys(existing.risk_dimension_narratives).length > 0) ||
    (next.risk_dimension_narratives && Object.keys(next.risk_dimension_narratives).length > 0)
      ? { ...(existing.risk_dimension_narratives ?? {}), ...(next.risk_dimension_narratives ?? {}) }
      : undefined
  const hasOutlookOrActions = (r: SupplierRow) =>
    (r.outlook != null && String(r.outlook).trim() !== '') || (r.actions != null && String(r.actions).trim() !== '')
  const scoreFromDetailed = hasOutlookOrActions(next) && next.overall_score != null ? next.overall_score : hasOutlookOrActions(existing) && existing.overall_score != null ? existing.overall_score : null
  const overall_score = scoreFromDetailed ?? next.overall_score ?? existing.overall_score ?? undefined
  return {
    name: next.name || existing.name,
    location: next.location ?? existing.location ?? null,
    risk_rating: next.risk_rating ?? existing.risk_rating ?? null,
    industry: next.industry ?? existing.industry ?? null,
    revenue: next.revenue ?? existing.revenue ?? null,
    growth: next.growth ?? existing.growth ?? null,
    employees: next.employees ?? existing.employees ?? null,
    years_in_business: next.years_in_business ?? existing.years_in_business ?? null,
    lead_time_days: next.lead_time_days ?? existing.lead_time_days ?? undefined,
    sla_pct: next.sla_pct ?? existing.sla_pct ?? undefined,
    quality_pct: next.quality_pct ?? existing.quality_pct ?? undefined,
    spend_k: next.spend_k ?? existing.spend_k ?? undefined,
    months_to_contract_expiry: next.months_to_contract_expiry ?? existing.months_to_contract_expiry ?? undefined,
    overall_score,
    outlook: next.outlook ?? existing.outlook ?? undefined,
    actions: next.actions ?? existing.actions ?? undefined,
    key_takeaways: next.key_takeaways ?? existing.key_takeaways ?? undefined,
    invoices: next.invoices ?? existing.invoices ?? undefined,
    support: next.support ?? existing.support ?? undefined,
    penalties_clause: next.penalties_clause ?? existing.penalties_clause ?? undefined,
    auto_renew: next.auto_renew ?? existing.auto_renew ?? undefined,
    stakeholder_score: next.stakeholder_score ?? existing.stakeholder_score ?? undefined,
    risk_scores: mergedRisk,
    supplier_unique_id: next.supplier_unique_id ?? existing.supplier_unique_id ?? undefined,
    website: next.website ?? existing.website ?? undefined,
    ownership: next.ownership ?? existing.ownership ?? undefined,
    kenvue_region: next.kenvue_region ?? existing.kenvue_region ?? undefined,
    plant_code: next.plant_code ?? existing.plant_code ?? undefined,
    plant_desc: next.plant_desc ?? existing.plant_desc ?? undefined,
    brand: next.brand ?? existing.brand ?? undefined,
    single_source: next.single_source ?? existing.single_source ?? undefined,
    category: next.category ?? existing.category ?? undefined,
    sub_category: next.sub_category ?? existing.sub_category ?? undefined,
    risk_gauge: next.risk_gauge ?? existing.risk_gauge ?? undefined,
    highest_risk_areas: next.highest_risk_areas ?? existing.highest_risk_areas ?? undefined,
    risk_outlook: next.risk_outlook ?? existing.risk_outlook ?? undefined,
    risk_outlook_6m: next.risk_outlook_6m ?? existing.risk_outlook_6m ?? undefined,
    risk_outlook_12m: next.risk_outlook_12m ?? existing.risk_outlook_12m ?? undefined,
    recommendations: next.recommendations ?? existing.recommendations ?? undefined,
    questions_to_ask: next.questions_to_ask ?? existing.questions_to_ask ?? undefined,
    methodology: next.methodology ?? existing.methodology ?? undefined,
    assessment_file_name: next.assessment_file_name ?? existing.assessment_file_name ?? undefined,
    report_title: next.report_title ?? existing.report_title ?? undefined,
    risk_dimension_narratives: mergedNarratives,
    dm_em: next.dm_em ?? existing.dm_em ?? undefined,
    procurement_type: next.procurement_type ?? existing.procurement_type ?? undefined,
    parent_name: next.parent_name ?? existing.parent_name ?? undefined,
    source_serial: next.source_serial ?? existing.source_serial ?? undefined,
  }
}

/**
 * True if the normalized sheet name indicates a dedicated Tasks or News sheet (exact or prefix with space).
 * Used to exclude these from the supplier sheet list without excluding e.g. "Supplier Risk Assessment and Mitigation Plan".
 */
export function isTasksOrNewsSheetName(normalizedName: string): boolean {
  const n = normalizedName.trim().toLowerCase().replace(/\s+/g, ' ')
  return (
    n === 'mitigation' ||
    n === 'tasks' ||
    n === 'news repository' ||
    n === 'news' ||
    n === 'news of suppliers' ||
    n === 'mitigation tracker' ||
    n.startsWith('mitigation ') ||
    n.startsWith('tasks ') ||
    n.startsWith('news repository ') ||
    n.startsWith('news of suppliers')
  )
}

/** True if workbook looks like Master Database / Kenvue Master layout. */
export function isMasterDatabaseWorkbook(sheetNames: string[]): boolean {
  const set = new Set(sheetNames.map((s) => s.trim().toLowerCase()))
  return set.has('intelligence view') || set.has('news of suppliers') || set.has('kenvue master')
}

/** One row from a Mitigation Tracker / Tasks sheet. */
export interface TaskRow {
  supplier_name: string
  task: string
  status: string
  priority: string
  alert_date?: string | null
  comments?: string | null
  category_manager?: string | null
  regional_head?: string | null
  plant_location?: string | null
  brand?: string | null
  supplier_unique_id?: string | null
}

/** One row from News Repository / News sheet. Entity Name = supplier name for supplier_id lookup. */
export interface NewsRow {
  entity_name: string
  headline: string
  description?: string | null
  region?: string | null
  source?: string | null
  external_url?: string | null
  risk_rating_numeric?: number | null
  published_at?: string | null
  type_of_risk?: string | null
  sentiment?: string | null
  key_concerns?: string[] | null
  parameter?: string | null
  emerging_themes?: string | null
  reason_for_mapping?: string | null
  risk_outlook_dec_may_2026?: string | null
  risk_outlook_dec_nov_2026?: string | null
  risk_dimensions?: Record<string, number | null> | null
  news_type?: string | null
  supplier_location?: string | null
  category?: string | null
  sub_category?: string | null
  supplier_number?: string | null
}

export interface UserGroupRow {
  function: string
  parent_group: string
  subgroup: string
}

export interface ParseResult {
  sheets: ParsedSheet[]
  supplierRows: SupplierRow[]
  /** Rows from a sheet named "Mitigation" or "Tasks", if present. */
  taskRows: TaskRow[]
  /** Rows from a sheet named "News Repository" or "News", if present. */
  newsRows: NewsRow[]
  /** User groups risk monitoring (Function / Parent Group / Subgroup). */
  userGroupRows: UserGroupRow[]
  errors: string[]
}

/** Infer direct|indirect from sheet title when Entity Type is not specific. */
export function inferProcurementTypeFromSheetName(sheetName: string): 'direct' | 'indirect' | null {
  const n = sheetName.trim().toLowerCase()
  if (/\bindirect\b/.test(n)) return 'indirect'
  if (/\bdirect\b/.test(n)) return 'direct'
  return null
}

/** Absolute USD Spend → spend_k ($K). Values already looking like $K stay as-is. */
export function spendUsdToSpendK(usd: number): number {
  if (!Number.isFinite(usd)) return usd
  // Workbook "Spend" is absolute USD (often >> 1k). Keep small values as already-$K.
  if (Math.abs(usd) >= 1000) return usd / 1000
  return usd
}

function isSupplierStructureSheet(keys: Set<string>, headerRow: string[]): boolean {
  const hasName =
    keys.has('name') ||
    headerRow.some((h) => {
      const n = normalizeHeader(h)
      return n.includes('supplier name') || n === 'name' || n.includes('entity name')
    })
  if (!hasName) return false
  return (
    keys.has('category') ||
    keys.has('spend_usd') ||
    keys.has('spend_k') ||
    keys.has('overall_score') ||
    keys.has('strategic') ||
    keys.has('financial') ||
    headerRow.some((h) => normalizeHeader(h) === 'final risk') ||
    headerRow.some((h) => normalizeHeader(h).includes('strategic risk'))
  )
}

function isUserGroupsSheet(headerRow: string[]): boolean {
  const norms = headerRow.map((h) => normalizeHeader(h))
  return (
    norms.some((h) => h === 'function') &&
    norms.some((h) => h === 'parent group' || h === 'parent_group') &&
    norms.some((h) => h === 'subgroup' || h === 'sub group' || h === 'sub-group')
  )
}

/** News sheet header aliases (Entity Name → supplier name for lookup). */
const NEWS_HEADER_ALIASES: Record<string, string> = {
  'entity name': 'entity_name',
  entity_name: 'entity_name',
  entity: 'entity_name',
  company: 'entity_name',
  'company name': 'entity_name',
  'name of the company': 'entity_name',
  headline: 'headline',
  'news headline': 'headline',
  summary: 'description',
  description: 'description',
  'region/country': 'region',
  'region/country ': 'region',
  region: 'region',
  country: 'region',
  'source / link': 'source',
  'source / link ': 'source',
  'source/link': 'source',
  source: 'source',
  link: 'external_url',
  'news link': 'external_url',
  external_url: 'external_url',
  'news type': 'news_type',
  news_type: 'news_type',
  'date published': 'published_at',
  'supplier location': 'supplier_location',
  category: 'category',
  'sub-category': 'sub_category',
  'supplier number': 'supplier_number',
  'risk rating (1-10)': 'risk_rating_numeric',
  'risk rating (1-10) ': 'risk_rating_numeric',
  'risk rating': 'risk_rating_numeric',
  risk_rating_numeric: 'risk_rating_numeric',
  date: 'published_at',
  published_at: 'published_at',
  'type of risk': 'type_of_risk',
  type_of_risk: 'type_of_risk',
  sentiment: 'sentiment',
  'sentiment (positive/negative)': 'sentiment',
  'sentiment (positive/negative) ': 'sentiment',
  'key takeaways': 'key_concerns',
  key_concerns: 'key_concerns',
  'next best action(s)': 'next_best_actions',
  'next best action(s) ': 'next_best_actions',
  'next best actions': 'next_best_actions',
  next_best_actions: 'next_best_actions',
  parameter: 'parameter',
  'emerging themes and patterns': 'emerging_themes',
  'emerging themes and patterns ': 'emerging_themes',
  emerging_themes: 'emerging_themes',
  'reason for mapping': 'reason_for_mapping',
  'reason for mapping ': 'reason_for_mapping',
  reason_for_mapping: 'reason_for_mapping',
  'risk outlook (dec 2025 - may 2026)': 'risk_outlook_dec_may_2026',
  'risk outlook (dec 2025 - may 2026) ': 'risk_outlook_dec_may_2026',
  risk_outlook_dec_may_2026: 'risk_outlook_dec_may_2026',
  'risk outlook (dec 2025 - nov 2026)': 'risk_outlook_dec_nov_2026',
  'risk outlook (dec 2025 - nov 2026) ': 'risk_outlook_dec_nov_2026',
  risk_outlook_dec_nov_2026: 'risk_outlook_dec_nov_2026',
  'risk rating (nov 2025)*': 'risk_rating_numeric',
  'risk rating (nov 2025)* ': 'risk_rating_numeric',
  'final risk rating (nov 14, 2025)': 'risk_rating_numeric',
  'final risk rating (nov 14, 2025) ': 'risk_rating_numeric',
}

/** Normalized CSV header names that map to risk_dimensions (14 risk types + 5 KPIs + Overall Vulnerability Index). */
const NEWS_RISK_DIMENSION_HEADERS = new Set([
  'compliance & legal risk',
  'geopolitical risk',
  'infrastructure & utilities risk',
  'labor & workforce risk',
  'strategic risk',
  'esg risk',
  'operational risk',
  'cybersecurity risk',
  'financial risk',
  'health & biological risk',
  'market risk',
  'natural hazard risk',
  'supply chain risk',
  'technology & innovation risk',
  'vendor performance kpi risk (1-10)',
  'sla compliance risk (1-10)',
  'contracts risk (1-10)',
  'stakeholder feedback risk (1-10)',
  'spend contribution risk (1-10)',
  'overall vulnerability index (0-10)',
])

const TASK_STATUSES = [
  'open',
  'on_hold',
  'closed',
  'scheduled',
  'in_progress',
  'at_risk',
  'completed',
] as const
const TASK_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const

/** True for Excel placeholders like Not Available / N/A (not bare "NA" — that is the North America region code). */
export function isExcelPlaceholder(value: unknown): boolean {
  if (value == null) return true
  const s = String(value).trim()
  if (!s) return true
  return /^not\s*available$/i.test(s) || /^n\/a$/i.test(s) || /^-+$/i.test(s)
}

/**
 * Normalize mitigation status for DB check constraint.
 * Blank/unknown → open; unknown free-text returned as overflowComment for comments.
 */
export function normalizeTaskStatus(s: string): { status: string; overflowComment?: string } {
  const trimmed = s.trim()
  if (!trimmed || isExcelPlaceholder(trimmed)) return { status: 'open' }
  const lower = trimmed.toLowerCase().replace(/\s+/g, '_')
  if (TASK_STATUSES.includes(lower as (typeof TASK_STATUSES)[number])) return { status: lower }
  if (lower === 'onhold' || lower.includes('on_hold') || lower.includes('hold')) return { status: 'on_hold' }
  if (lower.includes('progress')) return { status: 'in_progress' }
  if (lower.includes('complet') || lower.includes('close')) return { status: 'closed' }
  if (lower.includes('sched')) return { status: 'scheduled' }
  if (lower === 'open' || lower.includes('opened')) return { status: 'open' }
  // Free-text statuses from tracker (e.g. "No Impact…") are not valid enum values
  return { status: 'open', overflowComment: trimmed }
}

function normalizeTaskPriority(s: string): string {
  const lower = s.trim().toLowerCase()
  if (!lower || isExcelPlaceholder(lower)) return 'medium'
  if (TASK_PRIORITIES.includes(lower as (typeof TASK_PRIORITIES)[number])) return lower
  if (lower.includes('crit')) return 'critical'
  if (lower.includes('high')) return 'high'
  if (lower.includes('med')) return 'medium'
  if (lower.includes('low')) return 'low'
  return 'medium'
}

function normalizeHeader(h: string): string {
  return String(h ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
}

function normalizeKey(header: string): string | null {
  const n = normalizeHeader(header)
  const commentary = commentaryKeyFromHeader(n)
  if (commentary) return commentary
  return (SUPPLIER_HEADER_ALIASES[n] ?? n) || null
}

/** Set of all known supplier/risk column keys (from aliases + name). Used to detect real header rows vs title rows. */
const SUPPLIER_KNOWN_KEYS = new Set([
  'name',
  ...Object.values(SUPPLIER_HEADER_ALIASES),
])

/**
 * True if the row looks like a real column header row (at least 2 known keys including name),
 * not a title row like "Table 4: Supplier Internal Data Assessment".
 * Docs Excel: row 0 is often a title row; row 1 has "Supplier", "Location", "Vendor Performance KPI Risk (1-10)", etc.
 */
function isLikelyHeaderRow(row: string[]): boolean {
  const keys: string[] = []
  row.forEach((h, i) => {
    const k = normalizeKey(h) || (i === 0 ? 'name' : null)
    if (k && SUPPLIER_KNOWN_KEYS.has(k)) keys.push(k)
  })
  return keys.includes('name') && keys.length >= 2
}

/** True if row contains at least one Table 4 internal column header (Lead time, SLA, Quality, Spend). */
function rowHasTable4InternalHeaders(row: string[]): boolean {
  const table4Keys = new Set(['lead_time_days', 'sla_pct', 'quality_pct', 'spend_k'])
  return row.some((h) => {
    const k = normalizeKey(String(h ?? '').trim())
    return k != null && table4Keys.has(k)
  })
}

function normalizeTaskKey(header: string): string | null {
  const n = normalizeHeader(header)
  return TASK_HEADER_ALIASES[n] ?? null
}

function normalizeNewsKey(header: string): string | null {
  const n = normalizeHeader(header)
  return NEWS_HEADER_ALIASES[n] ?? null
}

function toNum(v: unknown): number | null {
  if (v == null || v === '') return null
  if (typeof v === 'number' && Number.isFinite(v)) return v
  const cleaned = String(v).replace(/[^\d.-]/g, '').trim()
  // Avoid Number('') === 0 for label cells like "Low risk"
  if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function toStr(v: unknown): string | null {
  if (v == null) return null
  const s = String(v).trim()
  return s === '' ? null : s
}

/** Parse Excel serial or date string to YYYY-MM-DD (UTC). */
export function parseNewsDate(raw: unknown): string | null {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const epoch = new Date(Date.UTC(1899, 11, 30))
    const d = new Date(epoch.getTime() + raw * 86400000)
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
    return null
  }
  const s = String(raw).trim()
  if (!s) return null
  const asNum = Number(s)
  if (Number.isFinite(asNum) && /^\d+(\.\d+)?$/.test(s) && asNum > 20000 && asNum < 100000) {
    const epoch = new Date(Date.UTC(1899, 11, 30))
    const d = new Date(epoch.getTime() + asNum * 86400000)
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  }
  // DD-Mon-YY or DD-Mon-YYYY (e.g. 20-Apr-26)
  const mon = s.match(/^(\d{1,2})[-/ ]([A-Za-z]{3})[-/ ](\d{2}|\d{4})$/)
  if (mon) {
    const day = Number(mon[1])
    const monthNames = 'jan feb mar apr may jun jul aug sep oct nov dec'.split(' ')
    const mi = monthNames.indexOf(mon[2].toLowerCase())
    let year = Number(mon[3])
    if (year < 100) year += year >= 70 ? 1900 : 2000
    if (mi >= 0 && day >= 1 && day <= 31) {
      const d = new Date(Date.UTC(year, mi, day))
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
    }
  }
  const d = new Date(s)
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return null
}

/**
 * Map a parsed sheet to supplier rows and validation errors. Use for sheet selector.
 */
export function getSupplierRowsFromSheet(sheet: ParsedSheet): { rows: SupplierRow[]; errors: string[] } {
  const errors: string[] = []
  const headerRow = sheet.headers as string[]
  const headerToKey: Record<number, string> = {}
  headerRow.forEach((h, i) => {
    const n = normalizeHeader(h)
    const fallbackName = i === 0 && (!n || n === 'supplier' || n === 'name' || n === 'entity name') ? 'name' : null
    const k = normalizeKey(h) || fallbackName
    if (k) headerToKey[i] = k
  })
  const rows = sheet.rows
    .map((r) => mapRowToSupplier(r))
    .filter((s): s is SupplierRow => s !== null)
  const hasName = rows.every((s) => s.name && String(s.name).trim())
  if (!hasName && rows.length > 0) {
    errors.push('Every row must have a supplier name (column "Supplier" or "Name").')
  }
  return { rows, errors }
}

/**
 * Parse an Excel file and return sheets, supplier rows from first data sheet, and optional task rows from "Mitigation"/"Tasks" sheet.
 */
export async function parseExcelFile(file: File): Promise<ParseResult> {
  const errors: string[] = []
  const buf = await file.arrayBuffer()
  const wb = XLSX.read(buf, { type: 'array', cellDates: false })

  const sheets: ParsedSheet[] = []
  let supplierRows: SupplierRow[] = []
  let taskRows: TaskRow[] = []
  let newsRows: NewsRow[] = []
  let userGroupRows: UserGroupRow[] = []
  const supplierByMergeKey = new Map<string, SupplierRow>()

  function addSupplierRows(mapped: SupplierRow[], sheetDefaultPt: 'direct' | 'indirect' | null) {
    for (const row of mapped) {
      const pt =
        row.procurement_type ??
        sheetDefaultPt ??
        (row.category &&
        ['Chemicals', 'Packaging', 'External Manufacturing'].includes(String(row.category))
          ? 'direct'
          : null)
      const withPt: SupplierRow = pt ? { ...row, procurement_type: pt } : row
      const key = supplierRowMergeKey(withPt)
      const prev = supplierByMergeKey.get(key)
      supplierByMergeKey.set(key, prev ? mergeSupplierRows(prev, withPt) : withPt)
    }
  }

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName]
    const data = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      defval: '',
      raw: false,
    }) as unknown[][]

    if (data.length < 2) continue

    // Find header row (docs Excel: row 0 is often "Table 4: Supplier Internal Data Assessment", row 1 has real column headers)
    let headerRowIndex = 0
    const firstRowCells = (data[0] ?? []) as string[]
    const firstRowNormalized = firstRowCells.map((c) => String(c ?? '').trim().toLowerCase())
    const firstRowHasSupplierPhrase =
      firstRowNormalized.some((c) => c.includes('supplier') || c === 'name' || c === 'entity name') ||
      firstRowNormalized.some((c) => c.includes('overall') && (c.includes('vulnerability') || c.includes('risk')))

    if (!firstRowHasSupplierPhrase && data.length > 1) {
      for (let i = 1; i < Math.min(data.length, 30); i++) {
        const row = (data[i] ?? []).map((c) => String(c ?? '').trim().toLowerCase())
        const rawRow = (data[i] ?? []) as string[]
        const hasSupplierOrOverall =
          row.some((c) => c.includes('supplier') || c === 'name') ||
          row.some((c) => c.includes('overall')) ||
          row.some((c) => c === 'final risk')
        if (hasSupplierOrOverall && (isLikelyHeaderRow(rawRow) || rowHasTable4InternalHeaders(rawRow))) {
          headerRowIndex = i
          break
        }
        if (row.some((c) => normalizeTaskKey(c))) {
          headerRowIndex = i
          break
        }
      }
    } else if (firstRowHasSupplierPhrase && data.length > 1 && !isLikelyHeaderRow(firstRowCells)) {
      // First row contains "supplier" (e.g. "Table 4: Supplier Internal Data Assessment") but is not a real header row
      for (let i = 1; i < Math.min(data.length, 10); i++) {
        const row = (data[i] ?? []) as string[]
        const hasSupplierOrOverall =
          row.some((c) => String(c).toLowerCase().includes('supplier')) ||
          row.some((c) => String(c).toLowerCase() === 'name') ||
          row.some((c) => String(c).toLowerCase().includes('overall'))
        if (hasSupplierOrOverall && (isLikelyHeaderRow(row) || rowHasTable4InternalHeaders(row))) {
          headerRowIndex = i
          break
        }
      }
    }

    const headerRow = (data[headerRowIndex] ?? []) as string[]
    const headerToKey: Record<number, string> = {}
    headerRow.forEach((h, i) => {
      const n = normalizeHeader(h)
      const fallbackName = i === 0 && (!n || n === 'supplier' || n === 'name' || n === 'entity name') ? 'name' : null
      const k = normalizeKey(h) || fallbackName
      if (k) headerToKey[i] = k
    })

    const rows: Record<string, unknown>[] = []
    for (let i = headerRowIndex + 1; i < data.length; i++) {
      const cells = (data[i] ?? []) as unknown[]
      const row: Record<string, unknown> = {}
      cells.forEach((cell, j) => {
        const key = headerToKey[j]
        if (key && (cell !== undefined && cell !== null && String(cell).trim() !== '')) {
          row[key] = cell
        }
      })
      if (Object.keys(row).length > 0) rows.push(row)
    }

    sheets.push({ name: sheetName, rows, headers: headerRow })

    const sn = String(sheetName).trim().toLowerCase().replace(/\s+/g, ' ')
    const isNewsSheet =
      sn === 'news repository' ||
      sn === 'news' ||
      sn === 'news of suppliers' ||
      sn.startsWith('news repository ') ||
      sn.startsWith('news of suppliers')
    if (isNewsSheet && rows.length > 0) {
      let newsHeaderRowIndex = headerRowIndex
      const hasNewsHeader =
        (headerRow as string[]).some(
          (c) =>
            normalizeNewsKey(c) === 'entity_name' ||
            normalizeNewsKey(c) === 'headline' ||
            normalizeNewsKey(c) === 'news_type'
        )
      if (!hasNewsHeader && data.length > 1) {
        for (let i = 0; i < Math.min(data.length, 25); i++) {
          const row = (data[i] ?? []).map((c) => String(c ?? '').trim())
          if (
            row.some((c) => normalizeNewsKey(c) === 'entity_name') ||
            row.some((c) => normalizeNewsKey(c) === 'headline') ||
            row.some((c) => normalizeNewsKey(c) === 'news_type')
          ) {
            newsHeaderRowIndex = i
            break
          }
        }
      }
      const newsHeaderRow = (data[newsHeaderRowIndex] ?? []) as string[]
      const newsHeaderToKey: Record<number, string> = {}
      newsHeaderRow.forEach((h, i) => {
        const k = normalizeNewsKey(h)
        if (k) newsHeaderToKey[i] = k
      })
      if (Object.keys(newsHeaderToKey).length > 0) {
        const parsed: NewsRow[] = []
        for (let i = newsHeaderRowIndex + 1; i < data.length; i++) {
          const cells = (data[i] ?? []) as unknown[]
          const row: Record<string, unknown> = {}
          cells.forEach((cell, j) => {
            const key = newsHeaderToKey[j]
            if (key && (cell !== undefined && cell !== null && String(cell).trim() !== '')) {
              row[key] = cell
            }
          })
          const entity_name = toStr(row.entity_name)
          const headline = toStr(row.headline)
          if (entity_name && headline) {
            const keyConcerns: string[] = []
            const kc = row.key_concerns
            if (typeof kc === 'string') keyConcerns.push(kc)
            else if (Array.isArray(kc)) keyConcerns.push(...kc.map((x) => String(x)).filter(Boolean))
            const next = toStr(row.next_best_actions)
            if (next) keyConcerns.push(next)
            const riskNum = toNum(row.risk_rating_numeric)
            const risk_dimensions: Record<string, number | null> = {}
            newsHeaderRow.forEach((header, j) => {
              const norm = normalizeHeader(header)
              if (NEWS_RISK_DIMENSION_HEADERS.has(norm)) {
                const v = toNum(cells[j])
                if (v != null) risk_dimensions[String(header).trim()] = v
              }
            })
            parsed.push({
              entity_name,
              headline,
              description: toStr(row.description) ?? undefined,
              region: toStr(row.region) ?? undefined,
              source: toStr(row.source) ?? undefined,
              external_url: toStr(row.external_url) ?? undefined,
              risk_rating_numeric: riskNum ?? undefined,
              published_at: parseNewsDate(row.published_at) ?? toStr(row.published_at) ?? undefined,
              type_of_risk: toStr(row.type_of_risk) ?? undefined,
              sentiment: toStr(row.sentiment) ?? undefined,
              key_concerns: keyConcerns.length > 0 ? keyConcerns : undefined,
              parameter: toStr(row.parameter) ?? undefined,
              emerging_themes: toStr(row.emerging_themes) ?? undefined,
              reason_for_mapping: toStr(row.reason_for_mapping) ?? undefined,
              risk_outlook_dec_may_2026: toStr(row.risk_outlook_dec_may_2026) ?? undefined,
              risk_outlook_dec_nov_2026: toStr(row.risk_outlook_dec_nov_2026) ?? undefined,
              risk_dimensions: Object.keys(risk_dimensions).length > 0 ? risk_dimensions : undefined,
              news_type: toStr(row.news_type) ?? undefined,
              supplier_location: toStr(row.supplier_location) ?? undefined,
              category: toStr(row.category) ?? undefined,
              sub_category: toStr(row.sub_category) ?? undefined,
              supplier_number: toStr(row.supplier_number) ?? undefined,
            })
          }
        }
        newsRows = parsed
      }
    }
    const isTasksSheet =
      sn === 'mitigation' ||
      sn === 'mitigation tracker' ||
      sn === 'tasks' ||
      sn.startsWith('mitigation ') ||
      sn.startsWith('tasks ')
    if (isTasksSheet && rows.length > 0) {
      const taskHeaderToKey: Record<number, string> = {}
      headerRow.forEach((h, i) => {
        const k = normalizeTaskKey(h)
        if (k) taskHeaderToKey[i] = k
      })
      if (Object.keys(taskHeaderToKey).length > 0) {
        const dataRows: Record<string, unknown>[] = []
        for (let i = headerRowIndex + 1; i < data.length; i++) {
          const cells = (data[i] ?? []) as unknown[]
          const row: Record<string, unknown> = {}
          cells.forEach((cell, j) => {
            const key = taskHeaderToKey[j]
            if (key && (cell !== undefined && cell !== null && String(cell).trim() !== '')) {
              row[key] = cell
            }
          })
          if (Object.keys(row).length > 0) dataRows.push(row)
        }
        taskRows = dataRows
          .map((r): TaskRow | null => {
            const supplier_name = toStr(r.supplier_name)
            const task = toStr(r.task)
            if (!supplier_name || !task) return null
            const statusRaw = String(r.status ?? '')
            const { status, overflowComment } = normalizeTaskStatus(statusRaw)
            let comments = toStr(r.comments)
            if (overflowComment) {
              comments = comments ? `${comments}\n${overflowComment}` : overflowComment
            }
            const uidRaw = toStr(r.supplier_unique_id)
            const brandRaw = toStr(r.brand)
            const locRaw = toStr(r.plant_location)
            const row: TaskRow = {
              supplier_name,
              task,
              status,
              priority: normalizeTaskPriority(String(r.priority ?? 'medium')),
              alert_date: parseNewsDate(r.alert_date) ?? toStr(r.alert_date),
              comments,
              category_manager: toStr(r.category_manager),
              regional_head: toStr(r.regional_head),
              plant_location: locRaw && !isExcelPlaceholder(locRaw) ? locRaw : null,
              brand: brandRaw && !isExcelPlaceholder(brandRaw) ? brandRaw : null,
              supplier_unique_id: uidRaw && !isExcelPlaceholder(uidRaw) ? uidRaw : null,
            }
            return row
          })
          .filter((t): t is TaskRow => t !== null)
      }
    }

    // User groups risk monitoring (Function / Parent Group / Subgroup)
    if (isUserGroupsSheet(headerRow) && rows.length > 0) {
      const groupHeaderToKey: Record<number, string> = {}
      headerRow.forEach((h, i) => {
        const n = normalizeHeader(h)
        if (n === 'function') groupHeaderToKey[i] = 'function'
        else if (n === 'parent group' || n === 'parent_group') groupHeaderToKey[i] = 'parent_group'
        else if (n === 'subgroup' || n === 'sub group' || n === 'sub-group') groupHeaderToKey[i] = 'subgroup'
      })
      const parsedGroups: UserGroupRow[] = []
      for (let i = headerRowIndex + 1; i < data.length; i++) {
        const cells = (data[i] ?? []) as unknown[]
        const row: Record<string, unknown> = {}
        cells.forEach((cell, j) => {
          const key = groupHeaderToKey[j]
          if (key && cell !== undefined && cell !== null && String(cell).trim() !== '') {
            row[key] = cell
          }
        })
        const fn = toStr(row.function)
        const pg = toStr(row.parent_group)
        const sg = toStr(row.subgroup)
        if (fn && pg && sg) parsedGroups.push({ function: fn, parent_group: pg, subgroup: sg })
      }
      if (parsedGroups.length > 0) userGroupRows = parsedGroups
    }

    // Map supplier sheets by structure (Direct/Indirect/Kenvue Master/Intelligence View) — merge all
    const isOnHoldSheet = /on[- ]?hold/i.test(sn)
    if (isOnHoldSheet) {
      errors.push(`Skipped On-Hold sheet (not loaded into live roster): "${sheetName}"`)
    }
    const isExcludedSheet =
      isOnHoldSheet ||
      sn === 'mitigation' ||
      sn === 'tasks' ||
      sn === 'mitigation tracker' ||
      isNewsSheet ||
      sn === 'risk assistant' ||
      sn === 'executive summary' ||
      sn === 'special reports' ||
      isUserGroupsSheet(headerRow)
    if (!isExcludedSheet && rows.length > 0) {
      const keys = new Set(Object.values(headerToKey))
      const isIntelligence =
        sn === 'intelligence view' ||
        sn.startsWith('intelligence view') ||
        sn === 'risk details' ||
        sn === 'kenvue master' ||
        sn.startsWith('kenvue master')
      if (isIntelligence || isSupplierStructureSheet(keys, headerRow as string[])) {
        const sheetDefaultPt = inferProcurementTypeFromSheetName(sheetName)
        const mapped = rows
          .map((r) => mapRowToSupplier(r, sheetDefaultPt))
          .filter((s): s is SupplierRow => s !== null)
        addSupplierRows(mapped, sheetDefaultPt)
      }
    }
  }

  supplierRows = [...supplierByMergeKey.values()]

  if (supplierRows.length === 0 && sheets.length > 0 && sheets[0].rows.length > 0) {
    const first = sheets[0]
    if (normalizeHeader(first.name) !== 'mitigation' && normalizeHeader(first.name) !== 'tasks') {
      const out = getSupplierRowsFromSheet(first)
      supplierRows = out.rows
      errors.push(...out.errors)
    }
  }

  const hasName = supplierRows.every((s) => s.name && String(s.name).trim())
  if (!hasName && supplierRows.length > 0) {
    errors.push('Every row must have a supplier name (column "Supplier" or "Name").')
  }

  return { sheets, supplierRows, taskRows, newsRows, userGroupRows, errors }
}

/** True if value looks like a weight/percentage cell (e.g. "5%", "10%") from Table 4, not a supplier name. */
function looksLikePercentageWeight(value: string): boolean {
  return /^\d+(\.\d+)?\s*%$/.test(String(value ?? '').trim())
}

/** True for Low / Moderate / Medium / High / Critical (optionally with "risk") gauge labels. */
export function isRiskGaugeLabel(value: unknown): boolean {
  if (value == null || value === '') return false
  const s = String(value).trim()
  if (!s || isExcelPlaceholder(s)) return false
  return /^(low|moderate|medium|high|critical)(\s+risk)?$/i.test(s) || /risk/i.test(s)
}

/**
 * Numeric / x/10 score only — never maps Low→2.5.
 * Use for Final Risk so "Not Available" and labels do not invent a score.
 */
export function parseNumericOrSlashScore(value: unknown): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const s = String(value).trim()
  if (isExcelPlaceholder(s)) return null
  if (isRiskGaugeLabel(s) && !/\d/.test(s)) return null
  const slash = s.match(/\(?\s*(\d+(?:\.\d+)?)\s*\/\s*10\s*\)?/i)
  if (slash) return Number(slash[1])
  return toNum(s)
}

/** Parse Risk Score like "4.2/10", "(2.2/10)", "Low risk", or plain number. */
export function parseRiskScore(value: unknown): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const s = String(value).trim()
  if (isExcelPlaceholder(s)) return null
  const slash = s.match(/\(?\s*(\d+(?:\.\d+)?)\s*\/\s*10\s*\)?/i)
  if (slash) return Number(slash[1])
  const low = s.toLowerCase()
  if (low.includes('critical')) return 9
  if (low.includes('high risk') || low === 'high') return 7.5
  if (low.includes('moderate') || low.includes('medium')) return 5
  if (low.includes('low risk') || low === 'low') return 2.5
  return toNum(s)
}

/**
 * Resolve overall_score + risk_gauge from Final Risk vs Overall Risk Rating columns.
 * Prefer Final Risk numeric/x/10 for score; labels go to gauge only (never overwrite a numeric Final Risk).
 */
export function resolveOverallScoreAndGauge(opts: {
  finalRisk?: unknown
  overallRiskRating?: unknown
  riskGauge?: unknown
}): { overall_score: number | null; risk_gauge: string | null } {
  const finalRaw = opts.finalRisk
  const ratingRaw = opts.overallRiskRating
  const finalCellPresent = finalRaw != null && String(finalRaw).trim() !== ''
  // Explicit "Not Available" means unscored — do not invent 2.5 from the Low label
  const finalIsPlaceholder = finalCellPresent && isExcelPlaceholder(finalRaw)
  const hasUsableFinal = finalCellPresent && !finalIsPlaceholder

  let overall_score = parseNumericOrSlashScore(finalRaw)
  if (overall_score == null && ratingRaw != null && !finalIsPlaceholder) {
    const ratingNumeric = parseNumericOrSlashScore(ratingRaw)
    if (ratingNumeric != null) {
      overall_score = ratingNumeric
    } else if (!hasUsableFinal && isRiskGaugeLabel(ratingRaw)) {
      // Legacy single-field sheets: label-only Overall Risk Rating → score fallback
      overall_score = parseRiskScore(ratingRaw)
    }
  }
  // Legacy: Final Risk cell is itself a label and no separate rating column
  if (
    overall_score == null &&
    hasUsableFinal &&
    isRiskGaugeLabel(finalRaw) &&
    (ratingRaw == null || String(ratingRaw).trim() === '')
  ) {
    overall_score = parseRiskScore(finalRaw)
  }

  let risk_gauge = cleanCellStr(opts.riskGauge ?? null)
  const looksLikeGaugeLabel = (s: string) =>
    /risk/i.test(s) || /^(low|moderate|medium|high|critical)$/i.test(s)
  if (!risk_gauge && ratingRaw != null && String(ratingRaw).trim() !== '') {
    const s = String(ratingRaw).trim()
    if (looksLikeGaugeLabel(s) && !isExcelPlaceholder(s)) risk_gauge = s
  }
  if (!risk_gauge && hasUsableFinal) {
    const s = String(finalRaw).trim()
    if (looksLikeGaugeLabel(s)) risk_gauge = s
  }
  return { overall_score, risk_gauge }
}

/** Extract Rating: X/10 from dimension narrative cells. */
export function parseDimensionRating(cellText: unknown): number | null {
  if (cellText == null || cellText === '') return null
  if (typeof cellText === 'number' && Number.isFinite(cellText)) return cellText
  const s = String(cellText)
  const m = s.match(/Rating:\s*(\d+(?:\.\d+)?)\s*\/\s*10/i)
  if (m) return Number(m[1])
  return toNum(s)
}

const MASTER_DIMENSION_KEYS = [
  'strategic',
  'financial',
  'compliance',
  'operational',
  'geopolitical',
  'infra',
  'supply_chain',
  'esg',
  'labor',
  'health_biological',
  'natural_hazard',
  'market',
  'tech_innovation',
  'cyber',
] as const

function cleanCellStr(value: unknown): string | null {
  const s = toStr(value)
  if (!s || isExcelPlaceholder(s)) return null
  return s
}

/** Canonical Sub-Category for the old "waxres" typo in master / taxonomy. */
export const CANONICAL_OTHER_CHEMICALS_WAXES = 'CHM - Other Chemicals (Waxes & new items)'

/** Fix waxres → Waxes; collapse known Other Chemicals variants to the taxonomy label. */
export function normalizeSubCategory(value: unknown): string | null {
  const s = cleanCellStr(value)
  if (!s) return null
  if (/waxres/i.test(s) && /other\s*chemicals/i.test(s)) return CANONICAL_OTHER_CHEMICALS_WAXES
  if (/waxres/i.test(s)) return s.replace(/waxres/gi, 'Waxes')
  return s
}

function mapRowToSupplier(
  row: Record<string, unknown>,
  sheetDefaultPt: 'direct' | 'indirect' | null = null
): SupplierRow | null {
  const get = (k: string): unknown => row[k] ?? null
  const name = cleanCellStr(get('name') ?? get('supplier'))
  if (!name) return null
  const nameNorm = normalizeHeader(name)
  if (SUPPLIER_NAME_BLOCKLIST.has(nameNorm)) return null
  if (looksLikePercentageWeight(name)) return null

  const lead_time_days = toNum(get('lead_time_days'))
  const sla_pct = toNum(get('sla_pct'))
  const quality_pct = toNum(get('quality_pct'))
  const spendKCol = toNum(get('spend_k'))
  const spendUsd = toNum(get('spend_usd'))
  const spend_k =
    spendKCol != null ? spendKCol : spendUsd != null ? spendUsdToSpendK(spendUsd) : null
  const months_to_contract_expiry = toNum(get('months_to_contract_expiry'))
  // Final Risk / vulnerability index → overall_score; Overall Risk Rating → separate key (see aliases)
  const finalRiskRaw = get('overall_score')
  const overallRiskRatingRaw = get('overall_risk_rating')
  const resolvedScore = resolveOverallScoreAndGauge({
    finalRisk: finalRiskRaw,
    overallRiskRating: overallRiskRatingRaw,
    riskGauge: get('risk_gauge'),
  })
  const overall_score = resolvedScore.overall_score

  const entityType = cleanCellStr(get('entity_type'))
  let procurement_type: 'direct' | 'indirect' | null = sheetDefaultPt
  if (entityType) {
    const et = entityType.toLowerCase()
    if (et.includes('indirect')) procurement_type = 'indirect'
    else if (et.includes('direct') && !et.includes('indirect')) procurement_type = 'direct'
  }

  const dm_em = cleanCellStr(get('dm_em'))
  const parent_name = cleanCellStr(get('parent_name'))

  const risk_scores: Record<string, unknown> = {}
  const risk_dimension_narratives: Record<string, string> = {}
  const riskKeys = [
    'vendor_performance_kpi_risk',
    'sla_compliance_risk',
    'contracts_risk',
    'stakeholder_feedback_risk',
    'spend_contribution_risk',
    ...MASTER_DIMENSION_KEYS,
  ]
  riskKeys.forEach((k) => {
    const raw = get(k)
    const v = parseDimensionRating(raw) ?? toNum(raw)
    if (v != null) risk_scores[k] = v
    if (raw != null && String(raw).trim() !== '' && typeof raw !== 'number') {
      const text = String(raw).trim()
      if (text.length > 20 || /Rating:/i.test(text)) risk_dimension_narratives[k] = text
    }
  })
  for (const k of MASTER_DIMENSION_KEYS) {
    const narr = toStr(get(`${k}_commentary`))
    if (narr) risk_dimension_narratives[k] = narr
  }

  // Prefer Country over Supplier Location when both columns exist (Indirect).
  const location = cleanCellStr(get('country')) ?? cleanCellStr(get('location'))
  const category = cleanCellStr(get('category'))
  const industry = cleanCellStr(get('industry')) ?? category
  const invoices = cleanCellStr(get('invoices'))
  const support = cleanCellStr(get('support'))
  const penalties_clause = cleanCellStr(get('penalties_clause'))
  const auto_renew = cleanCellStr(get('auto_renew'))
  const stakeholder_score = toNum(get('stakeholder_score'))
  let risk_gauge = resolvedScore.risk_gauge
  const risk_rating_raw = cleanCellStr(get('risk_rating'))
  const looksLikeGaugeLabel = (s: string) =>
    /risk/i.test(s) || /^(low|moderate|medium|high|critical)$/i.test(s)
  // Template "Risk category" maps to risk_rating; copy Medium/High/Low into risk_gauge for UI rollups
  if (!risk_gauge && risk_rating_raw && looksLikeGaugeLabel(risk_rating_raw)) {
    risk_gauge = risk_rating_raw
  }
  const outlook = cleanCellStr(get('outlook'))
  const actions = cleanCellStr(get('actions'))
  const highest_risk_areas = cleanCellStr(get('highest_risk_areas'))
  const singleRaw = get('single_source')
  let single_source: boolean | null = null
  if (singleRaw != null && !isExcelPlaceholder(singleRaw)) {
    const s = String(singleRaw).trim().toLowerCase()
    if (s === 'yes' || s === 'y' || s === 'true' || s === '1') single_source = true
    else if (s === 'no' || s === 'n' || s === 'false' || s === '0') single_source = false
  }

  const hasMeaningfulData =
    (location != null && location !== '') ||
    (industry != null && industry !== '') ||
    (category != null && category !== '') ||
    overall_score != null ||
    Object.keys(risk_scores).length > 0 ||
    (outlook != null && outlook !== '') ||
    (actions != null && actions !== '') ||
    (risk_gauge != null && risk_gauge !== '') ||
    lead_time_days != null ||
    sla_pct != null ||
    quality_pct != null ||
    spend_k != null
  if (!hasMeaningfulData) return null

  return {
    name,
    location: location ?? null,
    risk_rating: risk_rating_raw ?? risk_gauge ?? null,
    industry: industry ?? null,
    revenue: toStr(get('revenue')) ?? null,
    growth: toStr(get('growth')) ?? null,
    employees: toStr(get('employees')) ?? null,
    years_in_business: toNum(get('years_in_business')) ?? null,
    lead_time_days: lead_time_days ?? undefined,
    sla_pct: sla_pct ?? undefined,
    quality_pct: quality_pct ?? undefined,
    spend_k: spend_k ?? undefined,
    months_to_contract_expiry: months_to_contract_expiry ?? undefined,
    overall_score: overall_score ?? undefined,
    outlook: outlook ?? undefined,
    actions: actions ?? undefined,
    key_takeaways: toStr(get('key_takeaways')) ?? highest_risk_areas ?? undefined,
    invoices: invoices ?? undefined,
    support: support ?? undefined,
    penalties_clause: penalties_clause ?? undefined,
    auto_renew: auto_renew ?? undefined,
    stakeholder_score: stakeholder_score ?? undefined,
    risk_scores: Object.keys(risk_scores).length > 0 ? risk_scores : undefined,
    supplier_unique_id: cleanCellStr(get('supplier_unique_id')) ?? undefined,
    website: cleanCellStr(get('website')) ?? undefined,
    ownership: cleanCellStr(get('ownership')) ?? undefined,
    kenvue_region: cleanCellStr(get('kenvue_region')) ?? undefined,
    plant_code: cleanCellStr(get('plant_code')) ?? undefined,
    plant_desc: cleanCellStr(get('plant_desc')) ?? undefined,
    brand: cleanCellStr(get('brand')) ?? undefined,
    single_source: single_source ?? undefined,
    category: category ?? undefined,
    sub_category: normalizeSubCategory(get('sub_category')) ?? undefined,
    risk_gauge: risk_gauge ?? undefined,
    highest_risk_areas: highest_risk_areas ?? undefined,
    risk_outlook: cleanCellStr(get('risk_outlook')) ?? undefined,
    risk_outlook_6m: cleanCellStr(get('risk_outlook_6m')) ?? undefined,
    risk_outlook_12m: cleanCellStr(get('risk_outlook_12m')) ?? undefined,
    recommendations: cleanCellStr(get('recommendations')) ?? undefined,
    questions_to_ask: cleanCellStr(get('questions_to_ask')) ?? undefined,
    methodology: cleanCellStr(get('methodology')) ?? undefined,
    assessment_file_name: cleanCellStr(get('assessment_file_name')) ?? undefined,
    report_title: cleanCellStr(get('report_title')) ?? undefined,
    risk_dimension_narratives:
      Object.keys(risk_dimension_narratives).length > 0 ? risk_dimension_narratives : undefined,
    dm_em: dm_em ?? undefined,
    procurement_type: procurement_type ?? undefined,
    parent_name: parent_name ?? undefined,
    source_serial: cleanCellStr(get('source_serial')) ?? undefined,
  }
}

/** Build payload for supabase insert (company_id null, omit undefined). */
export function supplierRowsToInsertPayload(rows: SupplierRow[]): Record<string, unknown>[] {
  return rows.map((r) => {
    const payload: Record<string, unknown> = {
      company_id: null,
      name: r.name,
    }
    if (r.location != null) payload.location = r.location
    if (r.risk_rating != null) payload.risk_rating = r.risk_rating
    if (r.industry != null) payload.industry = r.industry
    if (r.revenue != null) payload.revenue = r.revenue
    if (r.growth != null) payload.growth = r.growth
    if (r.employees != null) payload.employees = r.employees
    if (r.years_in_business != null) payload.years_in_business = r.years_in_business
    if (r.lead_time_days != null) payload.lead_time_days = r.lead_time_days
    if (r.sla_pct != null) payload.sla_pct = r.sla_pct
    if (r.quality_pct != null) payload.quality_pct = r.quality_pct
    if (r.spend_k != null) payload.spend_k = r.spend_k
    if (r.months_to_contract_expiry != null) payload.months_to_contract_expiry = r.months_to_contract_expiry
    if (r.overall_score != null) payload.overall_score = r.overall_score
    if (r.outlook != null) payload.outlook = r.outlook
    if (r.actions != null) payload.actions = r.actions
    if (r.key_takeaways != null) payload.key_takeaways = r.key_takeaways
    if (r.invoices != null) payload.invoices = r.invoices
    if (r.support != null) payload.support = r.support
    if (r.penalties_clause != null) payload.penalties_clause = r.penalties_clause
    if (r.auto_renew != null) payload.auto_renew = r.auto_renew
    if (r.stakeholder_score != null) payload.stakeholder_score = r.stakeholder_score
    if (r.risk_scores != null && Object.keys(r.risk_scores).length > 0) payload.risk_scores = r.risk_scores
    if (r.supplier_unique_id != null) payload.supplier_unique_id = r.supplier_unique_id
    if (r.website != null) payload.website = r.website
    if (r.ownership != null) payload.ownership = r.ownership
    if (r.kenvue_region != null) payload.kenvue_region = r.kenvue_region
    if (r.plant_code != null) payload.plant_code = r.plant_code
    if (r.plant_desc != null) payload.plant_desc = r.plant_desc
    if (r.brand != null) payload.brand = r.brand
    if (r.single_source != null) payload.single_source = r.single_source
    if (r.category != null) payload.category = r.category
    if (r.sub_category != null) payload.sub_category = r.sub_category
    if (r.risk_gauge != null) payload.risk_gauge = r.risk_gauge
    if (r.highest_risk_areas != null) payload.highest_risk_areas = r.highest_risk_areas
    if (r.risk_outlook != null) payload.risk_outlook = r.risk_outlook
    if (r.risk_outlook_6m != null) payload.risk_outlook_6m = r.risk_outlook_6m
    if (r.risk_outlook_12m != null) payload.risk_outlook_12m = r.risk_outlook_12m
    if (r.recommendations != null) payload.recommendations = r.recommendations
    if (r.questions_to_ask != null) payload.questions_to_ask = r.questions_to_ask
    if (r.methodology != null) payload.methodology = r.methodology
    if (r.assessment_file_name != null) payload.assessment_file_name = r.assessment_file_name
    if (r.report_title != null) payload.report_title = r.report_title
    if (r.risk_dimension_narratives != null && Object.keys(r.risk_dimension_narratives).length > 0) {
      payload.risk_dimension_narratives = r.risk_dimension_narratives
    }
    if (r.dm_em != null) payload.dm_em = r.dm_em
    if (r.procurement_type != null) payload.procurement_type = r.procurement_type
    if (r.parent_name != null) payload.parent_name = r.parent_name
    return payload
  })
}

const CHUNK_SIZE = 100

/**
 * Insert supplier payloads in chunks. Returns { inserted, errors }.
 */
export async function insertSuppliersInChunks(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabaseClient: any,
  payloads: Record<string, unknown>[]
): Promise<{ inserted: number; errors: string[] }> {
  const errors: string[] = []
  let inserted = 0
  for (let i = 0; i < payloads.length; i += CHUNK_SIZE) {
    const chunk = payloads.slice(i, i + CHUNK_SIZE)
    const { error } = await supabaseClient.from('suppliers').insert(chunk)
    if (error) {
      errors.push(`Chunk ${Math.floor(i / CHUNK_SIZE) + 1}: ${String((error as Error).message)}`)
    } else {
      inserted += chunk.length
    }
  }
  return { inserted, errors }
}

const UPDATE_CHUNK_SIZE = 50

/**
 * Upsert supplier payloads: insert new, update existing matched by grain-safe keys
 * (Direct: serial or uid/name + brand + cat/sub + plant/geo; Indirect: uid+cat+sub / name+cat+sub).
 * Optionally assign all touched rows to assignCompanyId via supplier_companies.
 */
export async function upsertSuppliersInChunks(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabaseClient: any,
  payloads: Record<string, unknown>[],
  options?: { assignCompanyId?: string | null }
): Promise<{ inserted: number; updated: number; errors: string[]; supplierIds: string[] }> {
  const errors: string[] = []
  let inserted = 0
  let updated = 0
  const supplierIds: string[] = []
  const assignCompanyId = options?.assignCompanyId ?? null

  const names = [...new Set(payloads.map((p) => String(p.name ?? '').trim()).filter(Boolean))]
  if (names.length === 0) return { inserted: 0, updated: 0, errors: [], supplierIds: [] }

  type Existing = {
    id: string
    name: string
    brand?: string | null
    supplier_unique_id?: string | null
    category?: string | null
    sub_category?: string | null
    procurement_type?: string | null
    plant_desc?: string | null
    location?: string | null
    kenvue_region?: string | null
    dm_em?: string | null
  }
  const existing: Existing[] = []
  const NAME_FETCH = 150
  for (let i = 0; i < names.length; i += NAME_FETCH) {
    const nameChunk = names.slice(i, i + NAME_FETCH)
    const { data: existingRows, error: fetchError } = await supabaseClient
      .from('suppliers')
      .select(
        'id, name, brand, supplier_unique_id, category, sub_category, procurement_type, plant_desc, location, kenvue_region, dm_em'
      )
      .is('company_id', null)
      .in('name', nameChunk)
    if (fetchError) {
      errors.push(String(fetchError.message))
      return { inserted: 0, updated: 0, errors, supplierIds: [] }
    }
    existing.push(...((existingRows ?? []) as Existing[]))
  }

  const grainToId = new Map<string, string>()

  for (const row of existing) {
    const base = {
      name: row.name,
      brand: row.brand,
      supplier_unique_id: row.supplier_unique_id,
      category: row.category,
      sub_category: row.sub_category,
      procurement_type: row.procurement_type === 'indirect' ? 'indirect' : 'direct',
      plant_desc: row.plant_desc,
      location: row.location,
      kenvue_region: row.kenvue_region,
      dm_em: row.dm_em,
    } as const
    const key = supplierRowMergeKey(base)
    if (!grainToId.has(key)) grainToId.set(key, row.id)
    // Also index with empty region so null↔NA (North America) still matches after placeholder fixes.
    const keyNoReg = supplierRowMergeKey({ ...base, kenvue_region: null })
    if (!grainToId.has(keyNoReg)) grainToId.set(keyNoReg, row.id)
  }

  function matchExistingId(payload: Record<string, unknown>): string | undefined {
    const ptRaw = payload.procurement_type
    const procurement_type: 'direct' | 'indirect' | null =
      ptRaw === 'indirect' ? 'indirect' : ptRaw === 'direct' ? 'direct' : null
    const base: Parameters<typeof supplierRowMergeKey>[0] = {
      name: String(payload.name ?? ''),
      brand: payload.brand != null ? String(payload.brand) : null,
      supplier_unique_id:
        payload.supplier_unique_id != null ? String(payload.supplier_unique_id) : null,
      category: payload.category != null ? String(payload.category) : null,
      sub_category: payload.sub_category != null ? String(payload.sub_category) : null,
      procurement_type,
      plant_desc: payload.plant_desc != null ? String(payload.plant_desc) : null,
      location: payload.location != null ? String(payload.location) : null,
      kenvue_region: payload.kenvue_region != null ? String(payload.kenvue_region) : null,
      dm_em: payload.dm_em != null ? String(payload.dm_em) : null,
    }
    const id = grainToId.get(supplierRowMergeKey(base))
    if (id) return id
    return grainToId.get(supplierRowMergeKey({ ...base, kenvue_region: null }))
  }

  const toInsert: Record<string, unknown>[] = []
  const toUpdate: Array<{ id: string; payload: Record<string, unknown> }> = []

  for (const p of payloads) {
    const name = String(p.name ?? '').trim()
    if (!name) continue
    const id = matchExistingId(p)
    const payload = { ...p, name, company_id: null }
    if (id != null) {
      toUpdate.push({ id, payload })
    } else {
      toInsert.push(payload)
    }
  }

  for (let i = 0; i < toInsert.length; i += CHUNK_SIZE) {
    const chunk = toInsert.slice(i, i + CHUNK_SIZE)
    const { data, error } = await supabaseClient.from('suppliers').insert(chunk).select('id')
    if (error) {
      errors.push(`Insert chunk ${Math.floor(i / CHUNK_SIZE) + 1}: ${String((error as Error).message)}`)
    } else {
      inserted += chunk.length
      for (const row of data ?? []) {
        if (row?.id) supplierIds.push(row.id)
      }
    }
  }

  const updateFields = [
    'name',
    'location',
    'risk_rating',
    'industry',
    'revenue',
    'growth',
    'employees',
    'years_in_business',
    'lead_time_days',
    'sla_pct',
    'quality_pct',
    'spend_k',
    'months_to_contract_expiry',
    'overall_score',
    'outlook',
    'actions',
    'key_takeaways',
    'invoices',
    'support',
    'penalties_clause',
    'auto_renew',
    'stakeholder_score',
    'risk_scores',
    'company_id',
    'supplier_unique_id',
    'website',
    'ownership',
    'kenvue_region',
    'plant_code',
    'plant_desc',
    'brand',
    'single_source',
    'category',
    'sub_category',
    'risk_gauge',
    'highest_risk_areas',
    'risk_outlook',
    'risk_outlook_6m',
    'risk_outlook_12m',
    'recommendations',
    'questions_to_ask',
    'methodology',
    'assessment_file_name',
    'report_title',
    'risk_dimension_narratives',
    'dm_em',
    'procurement_type',
    'parent_name',
  ] as const

  const objectFields = new Set(['risk_scores', 'risk_dimension_narratives'])

  for (let i = 0; i < toUpdate.length; i += UPDATE_CHUNK_SIZE) {
    const chunk = toUpdate.slice(i, i + UPDATE_CHUNK_SIZE)
    for (const { id, payload } of chunk) {
      const updatePayload: Record<string, unknown> = {}
      for (const k of updateFields) {
        if (payload[k] === undefined) continue
        if (objectFields.has(k)) {
          const obj = payload[k] as Record<string, unknown> | undefined
          if (obj && typeof obj === 'object' && Object.keys(obj).length > 0) updatePayload[k] = payload[k]
          continue
        }
        updatePayload[k] = payload[k]
      }
      // Force re-embed after Master import updates (backfill only fills null).
      updatePayload.embedding = null
      const { error } = await supabaseClient.from('suppliers').update(updatePayload).eq('id', id)
      if (error) {
        errors.push(`Update ${String(payload.name)}: ${String((error as Error).message)}`)
      } else {
        updated++
        supplierIds.push(id)
      }
    }
  }

  if (assignCompanyId && supplierIds.length > 0) {
    const uniqueIds = [...new Set(supplierIds)]
    for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
      const chunk = uniqueIds.slice(i, i + CHUNK_SIZE).map((supplier_id) => ({
        supplier_id,
        company_id: assignCompanyId,
      }))
      const { error } = await supabaseClient
        .from('supplier_companies')
        .upsert(chunk, { onConflict: 'supplier_id,company_id' })
      if (error) {
        errors.push(`Assign company chunk ${Math.floor(i / CHUNK_SIZE) + 1}: ${String((error as Error).message)}`)
      }
    }
  }

  return { inserted, updated, errors, supplierIds }
}

/** Upsert user group rows for a company. */
export async function upsertUserGroupRows(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabaseClient: any,
  companyId: string,
  rows: UserGroupRow[]
): Promise<{ upserted: number; errors: string[] }> {
  const errors: string[] = []
  if (!companyId || rows.length === 0) return { upserted: 0, errors: [] }
  const payloads = rows.map((r) => ({
    company_id: companyId,
    function: r.function,
    parent_group: r.parent_group,
    subgroup: r.subgroup,
    updated_at: new Date().toISOString(),
  }))
  let upserted = 0
  for (let i = 0; i < payloads.length; i += CHUNK_SIZE) {
    const chunk = payloads.slice(i, i + CHUNK_SIZE)
    const { error } = await supabaseClient.from('supplier_risk_monitoring_groups').upsert(chunk, {
      onConflict: 'company_id,function,parent_group,subgroup',
    })
    if (error) {
      errors.push(`Groups chunk ${Math.floor(i / CHUNK_SIZE) + 1}: ${String((error as Error).message)}`)
    } else {
      upserted += chunk.length
    }
  }
  return { upserted, errors }
}

/** Build task insert payloads. Prefer supplier_unique_id match, then supplier name. */
export function taskRowsToInsertPayload(
  taskRows: TaskRow[],
  nameToId: Map<string, string>,
  companyId?: string | null,
  uniqueIdToId?: Map<string, string>
): { payloads: Record<string, unknown>[]; unresolved: string[] } {
  const payloads: Record<string, unknown>[] = []
  const unresolved: string[] = []
  const seen = new Set<string>()
  for (const row of taskRows) {
    let id: string | undefined
    const uid = row.supplier_unique_id?.trim()
    if (uid && uniqueIdToId?.has(uid)) {
      id = uniqueIdToId.get(uid)
    }
    if (!id) {
      id = nameToId.get(row.supplier_name.trim())
    }
    if (!id) {
      const lower = row.supplier_name.trim().toLowerCase()
      id = nameToId.get(lower)
    }
    if (!id) {
      if (!seen.has(row.supplier_name)) {
        seen.add(row.supplier_name)
        unresolved.push(row.supplier_name)
      }
      continue
    }
    const payload: Record<string, unknown> = {
      supplier_id: id,
      company_id: companyId ?? null,
      task: row.task,
      status: row.status,
      priority: row.priority,
    }
    if (row.alert_date) payload.alert_date = row.alert_date
    if (row.comments) payload.comments = row.comments
    if (row.category_manager) payload.category_manager = row.category_manager
    if (row.regional_head) payload.regional_head = row.regional_head
    if (row.plant_location) payload.plant_location = row.plant_location
    if (row.brand) payload.brand = row.brand
    payloads.push(payload)
  }
  return { payloads, unresolved }
}

/**
 * Insert mitigation task payloads in chunks. Returns { inserted, errors }.
 */
export async function insertMitigationTasksInChunks(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabaseClient: any,
  payloads: Record<string, unknown>[]
): Promise<{ inserted: number; errors: string[] }> {
  const errors: string[] = []
  let inserted = 0
  for (let i = 0; i < payloads.length; i += CHUNK_SIZE) {
    const chunk = payloads.slice(i, i + CHUNK_SIZE)
    const { error } = await supabaseClient.from('supplier_mitigation_tasks').insert(chunk)
    if (error) {
      errors.push(`Tasks chunk ${Math.floor(i / CHUNK_SIZE) + 1}: ${String((error as Error).message)}`)
    } else {
      inserted += chunk.length
    }
  }
  return { inserted, errors }
}

/** Map numeric risk 1–10 to High/Medium/Low for supplier_news.risk_level. */
function riskRatingToLevel(n: number): 'High' | 'Medium' | 'Low' | null {
  if (!Number.isFinite(n)) return null
  if (n >= 7) return 'High'
  if (n >= 4) return 'Medium'
  if (n >= 1) return 'Low'
  return null
}

/** Build supplier_news insert payloads from news rows and supplier name -> id map. Unmatched entity names get supplier_id null. */
export function newsRowsToInsertPayload(
  newsRows: NewsRow[],
  nameToId: Map<string, string>,
  companyId: string | null,
  supplierMeta?: Map<string, { location?: string | null; kenvue_region?: string | null; name?: string }>
): { payloads: Record<string, unknown>[]; unresolved: string[] } {
  const payloads: Record<string, unknown>[] = []
  const unresolved: string[] = []
  const seen = new Set<string>()

  const nameList: { id: string; name: string }[] = []
  const seenIds = new Set<string>()
  if (supplierMeta) {
    for (const [id, meta] of supplierMeta) {
      if (meta.name) nameList.push({ id, name: meta.name })
    }
  } else {
    for (const [k, id] of nameToId) {
      if (seenIds.has(id)) continue
      // Prefer original-case keys (contain uppercase or spaces with capitals)
      if (k !== k.toLowerCase() || /[A-Z]/.test(k)) {
        nameList.push({ id, name: k })
        seenIds.add(id)
      }
    }
    for (const [k, id] of nameToId) {
      if (seenIds.has(id)) continue
      nameList.push({ id, name: k })
      seenIds.add(id)
    }
  }

  for (const row of newsRows) {
    const nameKey = row.entity_name.trim()
    const id =
      nameToId.get(nameKey) ??
      nameToId.get(nameKey.toLowerCase()) ??
      matchSupplierId(nameKey, nameToId, nameList)
    if (!id) {
      if (!seen.has(row.entity_name)) {
        seen.add(row.entity_name)
        unresolved.push(row.entity_name)
      }
    }
    const riskLevel = row.risk_rating_numeric != null ? riskRatingToLevel(row.risk_rating_numeric) : null
    const regionRaw =
      row.region && !/^na$|^not classified$/i.test(String(row.region).trim())
        ? row.region
        : row.supplier_location && !/^na$|^not classified$/i.test(String(row.supplier_location).trim())
          ? row.supplier_location
          : null
    const meta = id && supplierMeta ? supplierMeta.get(id) : undefined
    const region = pickDisplayRegion(regionRaw, meta?.location, meta?.kenvue_region) ?? regionRaw
    const payload: Record<string, unknown> = {
      supplier_id: id ?? null,
      company_id: companyId,
      headline: row.headline,
      source: row.news_type != null ? 'master_database' : (row.source ?? 'excel'),
    }
    if (row.description != null) payload.description = row.description
    if (region != null) payload.region = region
    const coords = getCoordinatesForNews({
      region: region ?? undefined,
      supplierLocation: meta?.location ?? row.supplier_location ?? undefined,
      kenvueRegion: meta?.kenvue_region ?? undefined,
    })
    if (coords) {
      payload.lat = coords.lat
      payload.lng = coords.lng
    }
    const url = row.external_url && !/^na$/i.test(String(row.external_url).trim()) ? row.external_url : null
    if (url != null) payload.external_url = url
    if (riskLevel != null) payload.risk_level = riskLevel
    if (row.risk_rating_numeric != null) payload.risk_rating_numeric = row.risk_rating_numeric
    const publishedAt = parseNewsDate(row.published_at) ?? (row.published_at != null ? String(row.published_at) : null)
    if (publishedAt != null) payload.published_at = publishedAt
    if (row.key_concerns != null) payload.key_concerns = Array.isArray(row.key_concerns) ? row.key_concerns : [row.key_concerns]
    if (row.type_of_risk != null) payload.type_of_risk = row.type_of_risk
    if (row.sentiment != null) payload.sentiment = row.sentiment
    if (row.parameter != null) payload.parameter = row.parameter
    if (row.emerging_themes != null) payload.emerging_themes = row.emerging_themes
    if (row.reason_for_mapping != null) payload.reason_for_mapping = row.reason_for_mapping
    if (row.risk_outlook_dec_may_2026 != null) payload.risk_outlook_dec_may_2026 = row.risk_outlook_dec_may_2026
    if (row.risk_outlook_dec_nov_2026 != null) payload.risk_outlook_dec_nov_2026 = row.risk_outlook_dec_nov_2026
    if (row.risk_dimensions != null && Object.keys(row.risk_dimensions).length > 0) payload.risk_dimensions = row.risk_dimensions
    if (row.news_type != null) payload.news_type = row.news_type
    if (row.supplier_location != null) payload.supplier_location = row.supplier_location
    if (row.category != null) payload.category = row.category
    if (row.sub_category != null) payload.sub_category = row.sub_category
    if (row.supplier_number != null) payload.supplier_number = row.supplier_number
    payloads.push(payload)
  }
  return { payloads, unresolved }
}

const NEWS_CHUNK_SIZE = 50

/**
 * Filter out news payloads that already exist in supplier_news (same supplier_id + headline).
 * Call before insert to avoid importing duplicates.
 */
export async function filterExistingNewsPayloads(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabaseClient: any,
  payloads: Record<string, unknown>[]
): Promise<Record<string, unknown>[]> {
  if (payloads.length === 0) return []
  const supplierIds = [...new Set(payloads.map((p) => p.supplier_id).filter(Boolean))] as string[]
  const { data: existing } = await supabaseClient
    .from('supplier_news')
    .select('supplier_id, headline')
    .in('supplier_id', supplierIds)
  const existingKey = (sid: unknown, h: unknown) => `${sid}:${h}`
  const existingSet = new Set((existing ?? []).map((r: { supplier_id: string; headline: string }) => existingKey(r.supplier_id, r.headline)))
  return payloads.filter((p) => !existingSet.has(existingKey(p.supplier_id, p.headline)))
}

/**
 * Insert supplier_news rows in chunks. Returns { inserted, errors }.
 */
export async function insertNewsRowsInChunks(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabaseClient: any,
  payloads: Record<string, unknown>[]
): Promise<{ inserted: number; errors: string[] }> {
  const errors: string[] = []
  let inserted = 0
  for (let i = 0; i < payloads.length; i += NEWS_CHUNK_SIZE) {
    const chunk = payloads.slice(i, i + NEWS_CHUNK_SIZE)
    const { error } = await supabaseClient.from('supplier_news').insert(chunk)
    if (error) {
      errors.push(`News chunk ${Math.floor(i / NEWS_CHUNK_SIZE) + 1}: ${String((error as Error).message)}`)
    } else {
      inserted += chunk.length
    }
  }
  return { inserted, errors }
}
